import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import LeadFormButton from '../../../components/LeadFormButton';

const QualityControl = () => {
  const [activeCheckpoint, setActiveCheckpoint] = useState(0);

  const qualityCheckpoints = [
    {
      id: 1,
      title: "Inspeção de Matéria-Prima",
      stage: "Recebimento",
      description: "Verificação completa da qualidade da madeira antes do início do processo produtivo.",
      checks: [
        "Inspeção visual de defeitos",
        "Verificação de dimensões",
        "Análise de veios e fibras"
      ],
      icon: "Search",
      image: "https://images.unsplash.com/photo-1581539250439-c96689b516dd?w=600&h=400&fit=crop"
    },
    {
      id: 2,
      title: "Controle de Corte",
      stage: "Processamento",
      description: "Verificação da precisão dos cortes e conformidade com as especificações do projeto.",
      checks: [
        "Verificação de esquadro",
        "Inspeção de acabamento",
        "Análise de aproveitamento"
      ],
      icon: "Ruler",
      image: "https://images.unsplash.com/photo-1609205807107-e8ec2120f9de?w=600&h=400&fit=crop"
    },
    {
      id: 3,
      title: "Teste de Montagem",
      stage: "Pré-Montagem",
      description: "Verificação do encaixe perfeito de todas as partes antes da montagem final.",
      checks: [
        "Teste de encaixes",
        "Verificação de alinhamento",
        "Inspeção de juntas"
      ],
      icon: "Puzzle",
      image: "https://images.unsplash.com/photo-1581539250439-c96689b516dd?w=600&h=400&fit=crop"
    },
    {
      id: 4,
      title: "Controle de Acabamento",
      stage: "Finalização",
      description: "Inspeção detalhada da qualidade do acabamento e uniformidade da superfície.",
      checks: [
        "Uniformidade da cor",
        "Lisura da superfície",
        "Proteção adequada"
      ],
      icon: "Eye",
      image: "https://images.unsplash.com/photo-1609205807107-e8ec2120f9de?w=600&h=400&fit=crop"
    },
    {
      id: 5,
      title: "Inspeção Final",
      stage: "Entrega",
      description: "Verificação completa do móvel finalizado antes da entrega ao cliente.",
      checks: [
        "Funcionamento de gavetas",
        "Estabilidade estrutural",
        "Acabamento geral",
        "Embalagem adequada"
      ],
      icon: "CheckCircle",
      image: "https://images.unsplash.com/photo-1581539250439-c96689b516dd?w=600&h=400&fit=crop"
    }
  ];

  const certifications = [
    {
      name: "ISO 9001",
      description: "Sistema de Gestão da Qualidade",
      icon: "Award",
      color: "text-primary"
    },
    {
      name: "FSC Certified",
      description: "Madeira de Origem Sustentável",
      icon: "Leaf",
      color: "text-success"
    },
    {
      name: "ABIMÓVEL",
      description: "Associação Brasileira da Indústria do Móvel",
      icon: "Shield",
      color: "text-accent"
    },
    {
      name: "Garantia Vitalícia",
      description: "Estrutura e Acabamento",
      icon: "Infinity",
      color: "text-secondary"
    }
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
              <Icon name="ShieldCheck" size={24} color="var(--color-accent-foreground)" strokeWidth={2.5} />
            </div>
            <span className="font-inter text-accent font-semibold text-lg">Controle de Qualidade</span>
          </div>
          
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Excelência em Cada
            <span className="block text-accent">Etapa do Processo</span>
          </h2>
          
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Nosso rigoroso sistema de controle de qualidade garante que cada móvel atenda aos mais altos padrões de excelência antes de chegar até você.
          </p>
        </div>

        {/* Quality Checkpoints */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          {/* Checkpoint List */}
          <div className="space-y-4">
            <h3 className="font-playfair text-2xl font-bold text-primary mb-8">
              Pontos de Controle
            </h3>
            
            {qualityCheckpoints?.map((checkpoint, index) => (
              <div
                key={checkpoint?.id}
                className={`p-6 rounded-xl cursor-pointer transition-all duration-300 ${
                  activeCheckpoint === index
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'bg-card hover:bg-muted border border-border'
                }`}
                onClick={() => setActiveCheckpoint(index)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    activeCheckpoint === index
                      ? 'bg-white/20' :'bg-primary text-primary-foreground'
                  }`}>
                    <Icon name={checkpoint?.icon} size={20} strokeWidth={2.5} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-lg">{checkpoint?.title}</h4>
                      <span className={`text-sm px-3 py-1 rounded-full ${
                        activeCheckpoint === index
                          ? 'bg-white/20 text-primary-foreground'
                          : 'bg-muted text-text-secondary'
                      }`}>
                        {checkpoint?.stage}
                      </span>
                    </div>
                    
                    <p className={`text-sm leading-relaxed ${
                      activeCheckpoint === index
                        ? 'text-primary-foreground/80'
                        : 'text-text-secondary'
                    }`}>
                      {checkpoint?.description}
                    </p>
                    
                    <div className="flex items-center justify-end mt-3">
                      <Icon 
                        name="ChevronRight" 
                        size={16} 
                        className={`transition-transform duration-300 ${
                          activeCheckpoint === index ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Checkpoint Details */}
          <div className="bg-card rounded-2xl overflow-hidden shadow-lg border border-border sticky top-24">
            <div className="relative h-64">
              <Image
                src={qualityCheckpoints?.[activeCheckpoint]?.image}
                alt={`Controle: ${qualityCheckpoints?.[activeCheckpoint]?.title}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              
              <div className="absolute bottom-6 left-6 text-white">
                <div className="text-sm font-medium opacity-80">
                  {qualityCheckpoints?.[activeCheckpoint]?.stage}
                </div>
                <h3 className="text-xl font-bold">
                  {qualityCheckpoints?.[activeCheckpoint]?.title}
                </h3>
              </div>
            </div>

            <div className="p-8">
              <div className="mb-6">
                <h4 className="font-semibold text-primary mb-4 flex items-center space-x-2">
                  <Icon name="CheckSquare" size={20} />
                  <span>Verificações Realizadas</span>
                </h4>
                <ul className="space-y-3">
                  {qualityCheckpoints?.[activeCheckpoint]?.checks?.map((check, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <Icon name="Check" size={16} color="var(--color-success)" strokeWidth={2.5} />
                      <span className="text-text-secondary">{check}</span>
                    </li>
                  ))}
                </ul>
              </div>


              <Button
                variant="outline"
                fullWidth
                iconName="FileText"
                iconPosition="left"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              >
                Ver Checklist Completo
              </Button>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className="bg-card rounded-2xl p-8 md:p-12 shadow-lg border border-border">
          <div className="text-center mb-12">
            <h3 className="font-playfair text-2xl md:text-3xl font-bold text-primary mb-4">
              Certificações e Garantias
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Nosso compromisso com a qualidade é reconhecido por certificações nacionais e internacionais.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications?.map((cert, index) => (
              <div key={index} className="text-center group">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  cert?.color === 'text-primary' ? 'bg-primary/10' :
                  cert?.color === 'text-success' ? 'bg-success/10' :
                  cert?.color === 'text-accent'? 'bg-accent/10' : 'bg-secondary/10'
                }`}>
                  <Icon name={cert?.icon} size={28} className={cert?.color} strokeWidth={2.5} />
                </div>
                <h4 className="font-semibold text-primary mb-2">{cert?.name}</h4>
                <p className="text-sm text-text-secondary">{cert?.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-primary to-trust rounded-2xl p-12 text-white">
            <h3 className="font-playfair text-3xl font-bold mb-4">
              Qualidade Garantida por Toda Vida
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Oferecemos garantia vitalícia na estrutura e 5 anos no acabamento. Sua satisfação é nossa prioridade.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Shield"
                iconPosition="left"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                Conhecer Garantias
              </Button>
              <LeadFormButton
                variant="outline"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                className="border-white text-white hover:bg-white hover:text-primary"
              >
                Falar com Especialista
              </LeadFormButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QualityControl;